import * as React from 'react';
import { Button } from '../primitives/button';
import { Input } from '../primitives/input';
import { Checkbox } from '../primitives/checkbox';
import { Calendar, fromISODate, toISODate } from '../primitives/calendar';
import { cn } from '../../lib/utils';
import type { FilterField, FilterValue } from './types';

interface FilterEditorProps {
  field: FilterField;
  value?: FilterValue;
  /** The value changed. Does not imply the person is finished. */
  onApply: (_value: FilterValue) => void;
  /** Finished — close the editor. */
  onCommit: () => void;
  /** Abandon this filter entirely. */
  onClear: () => void;
}

/**
 * The value editor for one field. Each field type gets the control that suits
 * it — a checklist, a pair of bounds, presets plus a range — while the chip
 * grammar around it stays the same.
 *
 * Changing a value and being finished are separate: a multi-select stays open
 * while several values are ticked, whereas a single choice or a date preset is
 * complete the moment it is made.
 */
export function FilterEditor({ field, value, onApply, onCommit, onClear }: FilterEditorProps) {
  if (field.type === 'enum') {
    const selected = value?.type === 'enum' ? value.values : [];
    const toggle = (candidate: string, checked: boolean) => {
      if (!field.multiple) {
        onApply({ type: 'enum', values: [candidate] });
        onCommit();
        return;
      }
      onApply({
        type: 'enum',
        values: checked
          ? [...selected, candidate]
          : selected.filter((existing) => existing !== candidate),
      });
    };

    return (
      <div className="min-w-[11rem] p-1">
        {field.options.map((option) => {
          const id = `filter-${field.key}-${option.value}`;
          return (
            <label
              key={option.value}
              htmlFor={id}
              data-testid={`filter-option-${option.value}`}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm hover:bg-muted"
            >
              <Checkbox
                id={id}
                checked={selected.includes(option.value)}
                onCheckedChange={(next) => toggle(option.value, next === true)}
              />
              <span className="text-foreground">{option.label}</span>
            </label>
          );
        })}
        <EditorFooter onClear={onClear} onDone={field.multiple ? onCommit : undefined} />
      </div>
    );
  }

  if (field.type === 'number') {
    return (
      <NumberEditor
        field={field}
        value={value}
        onApply={onApply}
        onCommit={onCommit}
        onClear={onClear}
      />
    );
  }

  return (
    <DateEditor field={field} value={value} onApply={onApply} onCommit={onCommit} onClear={onClear} />
  );
}

function EditorFooter({ onClear, onDone }: { onClear: () => void; onDone?: () => void }) {
  return (
    <div className="mt-1 flex items-center justify-end gap-2 border-t border-border px-2 pt-2">
      <Button variant="ghost" size="sm" onClick={onClear} data-testid="filter-clear">
        Clear
      </Button>
      {onDone && (
        <Button size="sm" onClick={onDone} data-testid="filter-apply">
          Apply
        </Button>
      )}
    </div>
  );
}

/** An empty box means "no bound"; 0 is a real bound, so parse rather than relying on falsiness. */
function parseBound(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? undefined : parsed;
}

function NumberEditor({
  field,
  value,
  onApply,
  onCommit,
  onClear,
}: FilterEditorProps & { field: Extract<FilterField, { type: 'number' }> }) {
  const current = value?.type === 'number' ? value : undefined;
  const [min, setMin] = React.useState(current?.min?.toString() ?? '');
  const [max, setMax] = React.useState(current?.max?.toString() ?? '');

  const commit = () => {
    const lower = parseBound(min);
    const upper = parseBound(max);
    // "$100 to $10" was applied exactly as typed and then matched nothing, so
    // the seller got an empty list and no reason for it. The ends swap, which
    // is what the date range already does when the earlier day is picked
    // second — one rule for both, and the chip reads back the right way round.
    const reversed = lower !== undefined && upper !== undefined && lower > upper;
    onApply({
      type: 'number',
      min: reversed ? upper : lower,
      max: reversed ? lower : upper,
    });
    onCommit();
  };

  return (
    <div className="w-60 p-3">
      <div className="flex items-center gap-2">
        <Input
          aria-label={`${field.label} minimum`}
          data-testid="filter-number-min"
          placeholder="Min"
          inputMode="decimal"
          prefix={field.prefix}
          value={min}
          onChange={(event) => setMin(event.target.value)}
        />
        <span className="text-xs text-muted-foreground">to</span>
        <Input
          aria-label={`${field.label} maximum`}
          data-testid="filter-number-max"
          placeholder="Max"
          inputMode="decimal"
          prefix={field.prefix}
          value={max}
          onChange={(event) => setMax(event.target.value)}
        />
      </div>
      <EditorFooter onClear={onClear} onDone={commit} />
    </div>
  );
}

/**
 * Presets down the left, a calendar on the right, and the exact dates spelled
 * out underneath — the shape the Shopify admin app's DateRangePicker already
 * uses, minus Polaris, which partner-web does not have.
 *
 * The whole editor used to be two `<input type="date">` boxes: unstylable,
 * inconsistent between browsers, and impossible to show in a story. Almost
 * every real use is a preset, so those lead; the calendar is there for the
 * cases that aren't, and the two text fields keep the chosen range readable
 * and typeable.
 */
function DateEditor({
  field,
  value,
  onApply,
  onCommit,
  onClear,
}: FilterEditorProps & { field: Extract<FilterField, { type: 'date' }> }) {
  const current = value?.type === 'date' ? value : undefined;
  const [from, setFrom] = React.useState(current?.from ?? '');
  const [to, setTo] = React.useState(current?.to ?? '');
  // Two months are shown, and this is the left-hand one. A "created" or
  // "placed" filter only looks backwards, so anchoring on the current month
  // put the whole right-hand calendar in the future with every day disabled —
  // half the control, useless. Last month and this month is the pair that has
  // days in it.
  const [month, setMonth] = React.useState(() => {
    const anchor = fromISODate(current?.from) ?? new Date();
    return new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
  });

  const fromDate = fromISODate(from);
  const toDate = fromISODate(to);
  const today = React.useMemo(() => new Date(), []);

  // Click once to start a range, again to close it, again to start over. The
  // second click filling in whichever end is missing is the behaviour every
  // range picker has; anything cleverer needs explaining.
  const pickDay = (date: Date) => {
    const iso = toISODate(date);
    if (!fromDate || (fromDate && toDate)) {
      setFrom(iso);
      setTo('');
      return;
    }
    if (date < fromDate) {
      setFrom(iso);
      setTo(toISODate(fromDate));
      return;
    }
    setTo(iso);
  };

  const applyRange = () => {
    onApply({ type: 'date', from: from || undefined, to: to || undefined });
    onCommit();
  };

  return (
    <div className="flex" data-testid="filter-date-editor">
      {field.presets && field.presets.length > 0 && (
        <div className="w-[9.5rem] shrink-0 border-r border-border p-1.5">
          {field.presets.map((preset) => {
            const active = current?.presetDays === preset.days;
            return (
              <button
                key={preset.days}
                type="button"
                data-testid={`filter-preset-${preset.days}`}
                aria-pressed={active}
                onClick={() => {
                  // A preset is a complete choice, so it finishes the edit.
                  onApply({ type: 'date', presetDays: preset.days });
                  onCommit();
                }}
                className={cn(
                  'block w-full rounded-sm px-2.5 py-1.5 text-left text-xs transition-colors',
                  active
                    ? 'bg-primary/10 font-medium text-primary'
                    : 'text-foreground hover:bg-muted',
                )}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="p-3">
        {/* What is being filtered and how, stated once at the top. The editor
            opened onto a column of presets and a calendar with nothing naming
            the field — fine while there is one date on the screen, wrong the
            moment there are two. */}
        <p className="mb-2 text-xs text-muted-foreground">
          <b className="font-semibold text-foreground">{field.label}</b>
          {' · date'}
        </p>
        <Calendar
          month={month}
          onMonthChange={setMonth}
          from={fromDate}
          to={toDate}
          maxDate={today}
          onSelect={pickDay}
        />

        {/* The presets answer most questions; this says the other way is here. */}
        <p className="mb-1.5 mt-3 text-xs text-muted-foreground">or a range</p>
        <div className="flex items-center gap-2">
          <Input
            aria-label={`${field.label} from`}
            data-testid="filter-date-from"
            placeholder="YYYY-MM-DD"
            className="h-8 text-xs"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            aria-label={`${field.label} to`}
            data-testid="filter-date-to"
            // "Today", not the format again: the placeholder's job on the end
            // of an open range is to say what leaving it blank means.
            placeholder="Today"
            className="h-8 text-xs"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>

        {/* An explicit range supersedes any preset. */}
        <EditorFooter onClear={onClear} onDone={applyRange} />
      </div>
    </div>
  );
}
